import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { ROLES, REGIONS, PLAN_OPTIONS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  full_name: "",
  email: "",
  phone: "",
  role: ROLES[0],
  region: REGIONS[0],
  plan: PLAN_OPTIONS[0],
  message: "",
};

export const JoinDialog = ({ open, onClose }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/join`, form);
      setDone(data.reference);
      setForm(EMPTY);
      toast.success("Registration received", { description: `Reference ${data.reference}` });
    } catch {
      toast.error("Submission failed", { description: "Please check your details and try again." });
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    onClose();
    setTimeout(() => setDone(null), 350);
  };

  const field =
    "w-full bg-transparent border-b border-[rgba(244,241,230,0.26)] py-2.5 text-sm outline-none focus:border-[color:var(--kmg-lime)] placeholder:text-[color:var(--kmg-bone-dim)] placeholder:opacity-55";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="join-dialog"
        >
          <div className="absolute inset-0 bg-[#040d09]/88 backdrop-blur-sm" onClick={close} />
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-2xl border border-[rgba(111,227,155,0.25)] bg-[color:var(--kmg-deep)] p-8 sm:p-11 max-h-[90vh] overflow-y-auto"
          >
            <button onClick={close} data-testid="join-dialog-close" className="absolute top-4 right-4 p-2 text-[color:var(--kmg-bone-dim)] hover:text-[color:var(--kmg-lime)]" aria-label="Close">
              <X size={18} />
            </button>

            {done ? (
              <div className="py-10 text-center" data-testid="join-success">
                <CheckCircle2 size={44} className="mx-auto text-[color:var(--kmg-lime)]" />
                <h3 className="font-display text-2xl mt-6">Registration received</h3>
                <p className="text-sm mt-4 text-[color:var(--kmg-bone-dim)]">
                  Your reference is <span className="font-mono-label text-[color:var(--kmg-lime)]">{done}</span>. A KMG
                  Digi Platform representative will be in touch shortly.
                </p>
                <button onClick={close} data-testid="join-success-close" className="mt-9 rounded-full bg-[color:var(--kmg-lime)] px-8 py-3 text-sm font-semibold text-[color:var(--kmg-forest)]">
                  Done
                </button>
              </div>
            ) : (
              <>
                <p className="eyebrow">Join the KMG Digi Platform today</p>
                <h3 className="font-display text-2xl sm:text-3xl mt-4 leading-snug">Start your smart farming journey.</h3>

                <form onSubmit={submit} className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-7" data-testid="join-form">
                  <input required value={form.full_name} onChange={set("full_name")} placeholder="Full name" data-testid="join-name-input" className={field} />
                  <input required type="email" value={form.email} onChange={set("email")} placeholder="Email address" data-testid="join-email-input" className={field} />
                  <input required value={form.phone} onChange={set("phone")} placeholder="Phone number" data-testid="join-phone-input" className={field} />
                  <select value={form.role} onChange={set("role")} data-testid="join-role-select" className={`${field} text-[color:var(--kmg-bone)]`}>
                    {ROLES.map((r) => (
                      <option key={r} value={r} className="bg-[color:var(--kmg-deep)]">{r}</option>
                    ))}
                  </select>
                  <select value={form.region} onChange={set("region")} data-testid="join-region-select" className={`${field} text-[color:var(--kmg-bone)]`}>
                    {REGIONS.map((r) => (
                      <option key={r} value={r} className="bg-[color:var(--kmg-deep)]">{r}</option>
                    ))}
                  </select>
                  <select value={form.plan} onChange={set("plan")} data-testid="join-plan-select" className={`${field} text-[color:var(--kmg-bone)]`}>
                    {PLAN_OPTIONS.map((r) => (
                      <option key={r} value={r} className="bg-[color:var(--kmg-deep)]">{r}</option>
                    ))}
                  </select>
                  <textarea
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Tell us about your farm or business (optional)"
                    rows={3}
                    data-testid="join-message-input"
                    className={`${field} sm:col-span-2 resize-none`}
                  />
                  <button
                    type="submit"
                    disabled={busy}
                    data-testid="join-submit-btn"
                    className="sm:col-span-2 mt-3 rounded-full bg-[color:var(--kmg-lime)] px-8 py-4 text-sm font-semibold text-[color:var(--kmg-forest)] hover:bg-[#8df0b3] disabled:opacity-60"
                    style={{ transition: "background-color .4s ease" }}
                  >
                    {busy ? "Submitting…" : "Register now"}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
